import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import SnackbarUtils from 'utils/SnackbarUtils';
import { Col } from '../../../components/Col';
import { Container } from '../../../components/Container';
import { CustomButton } from '../../../components/CustomButton';
import { CustomCard } from '../../../components/CustomCard';
import IdebTable from '../../../components/Idebs/IdebTable';
import { Row } from '../../../components/Row';
import UnauthorizedScreen from '../../../components/UnauthorizedScreen/UnauthorizedScreen';
import { escolasRoute } from '../../../library/Routes';
import { loadAllIdebById } from '../../../store/modules/EscolasActions';
import { openModal } from '../../../store/modules/ModalActions';
import { isAuthorizedRequest } from '../../../utils/httpResponseHandlers';
import { getElementID } from '../../../utils/HTMLUtils';
import IdebEditModal from '../IdebEditModal/IdebEditModal';
import dadosDaEscolaBlackIcon from './../../../assets/images/svg/profile-black-icon.svg';
import styles from './Ideb.scss';

const rootClassName = styles.rootClassName;
const rootElementId = 'ideb';

const Ideb = () => {
  const [authorizedRequest, setAuthorizedRequest] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const languageValues = useSelector((state) => state.languages.languageValues);
  const ideb = useSelector((state) => state.escolas.allIdebs.ideb);
  const auth = useSelector((state) => state.auth);

  const changeLoadingStatus = (loadingParam, moreProps = {}) => {
    setLoading(loadingParam);
    setAuthorizedRequest(
      moreProps.authorizedRequest !== undefined
        ? moreProps.authorizedRequest
        : authorizedRequest
    );
  };

  const loadIdeb = () => {
    changeLoadingStatus(true);
    dispatch(
      loadAllIdebById(
        id,
        () => {
          changeLoadingStatus(false);
        },
        (error) => {
          if (isAuthorizedRequest(error.response)) {
            changeLoadingStatus(false);
            SnackbarUtils.enqueueSnackbar({
              message:
                error.response.statusText || languageValues.errorSearchingIdeb,
              options: {
                variant: 'error',
              },
            });
          } else {
            changeLoadingStatus(false, { authorizedRequest: false });
          }
        }
      )
    );
  };

  useEffect(() => {
    loadIdeb();
    const updateDimensions = () => {
      setIsMobile(window.innerWidth < parseInt(styles.xsSize) + 1);
    };
    window.addEventListener('resize', updateDimensions);
    updateDimensions();
    return () => window.removeEventListener('resize', updateDimensions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const allowRegisterButton = () => auth.userPermissions.escolas.ideb.register;

  const getContainer = (children) => {
    if (isMobile) {
      return (
        <Container className={rootClassName + '-container'}>
          {children}
        </Container>
      );
    }
    return (
      <Container
        className={rootClassName + '-container container-fluid'}
        style={{ marginTop: '80px' }}
      >
        <div className='container-fluid'>
          <Row className={rootClassName + '-header'}>
            <Col cols={[12, 12, 5, 4, 4]}>{getRegisterButton()}</Col>
          </Row>
          {children}
          <Row className={rootClassName + '-footer'}>
            <Col cols={[12, 12, 5, 4, 4]}>{getBackToSchoolsButton()}</Col>
          </Row>
        </div>
      </Container>
    );
  };

  const getRegisterButton = () => {
    return allowRegisterButton() ? (
      <CustomButton
        id={getElementID(rootElementId, 'create')}
        theme='pufvYellow'
        className={rootClassName + '-create-button'}
        label={languageValues.newIdebButton}
        rightIcon={<img src={dadosDaEscolaBlackIcon} alt='icone-dados-escola' loading='lazy' />}
        onClick={openIdeb}
      />
    ) : null;
  };

  const openIdeb = () => {
    dispatch(
      openModal(IdebEditModal, {
        idEscola: id,
        savedCallback: refreshList,
      })
    );
  };

  const getBackToSchoolsButton = () => {
    return (
      <CustomButton
        id={getElementID(rootElementId, 'back')}
        theme='pufvYellow'
        className={rootClassName + '-create-button'}
        label={languageValues.newVoltarEscolaButton}
        rightIcon={<img src={dadosDaEscolaBlackIcon} alt='icone-dados-escola' loading='lazy'/>}
        onClick={() => history.push(escolasRoute.path)}
      />
    );
  };

  const refreshList = () => {
    loadIdeb();
  };

  const getIdebs = () => {
    if (typeof id === 'string' && Array.isArray(ideb)) {
      return ideb;
    }
    return [];
  };

  return authorizedRequest && auth.userPermissions.escolas.ideb.list ? (
    getContainer(
      <CustomCard
        CardPropTypes={{
          className: rootClassName + '-card',
        }}
        CardContentPropTypes={{
          className: rootClassName + '-card-content',
        }}
        CardHeaderPropTypes={{
          className: rootClassName + '-card-header',
          title: languageValues.idebTitle,
        }}
        titleHint={languageValues.idebHint}
        sending={loading}
      >
        {isMobile ? getRegisterButton() : null}
        {isMobile ? getBackToSchoolsButton() : null}

        {getIdebs().length > 0 ? (
          <IdebTable
            id={rootElementId}
            idebs={getIdebs()}
            className={rootClassName + '-periodos'}
            savedCallback={refreshList}
          />
        ) : (
          <p className={rootClassName + '-empty-message'}>
            {languageValues.emptyIdebsMessage}
          </p>
        )}
      </CustomCard>
    )
  ) : (
    <UnauthorizedScreen />
  );
};

export default Ideb;
